/**
 * Schedule Utilities
 *
 * Helper functions for parsing, validating, and manipulating playlist schedules.
 * These utilities bridge the gap between backend JSON strings and typed frontend objects.
 *
 * Azure Best Practice: Defensive programming with type guards and comprehensive error handling
 */

import { Playlist } from "./playlist.types";
import {
  Schedule,
  ScheduleType,
  PriorityLevel,
  AlwaysSchedule,
  TimeWindowSchedule,
  CrossDayTimeWindowSchedule,
  PrayerWindowSchedule,
  CrossDayPrayerWindowSchedule,
  PrayerBasedSchedule,
  HijriDateSchedule,
  GregorianDateSchedule,
  EventBasedSchedule,
  RepeatType,
} from "./schedule.types";

// ============================================================================
// SCHEDULE PARSING & VALIDATION
// ============================================================================

/**
 * Ensure playlist schedule is parsed from JSON string to typed object
 *
 * Backend sends scheduleJson as string, this ensures it's parsed and typed correctly.
 * Falls back to AlwaysSchedule if parsing fails or data is missing.
 *
 * Azure Best Practice: Defensive programming with graceful fallback
 *
 * @param playlist Playlist with potentially unparsed schedule
 * @returns Playlist with guaranteed parsed schedule object
 *
 * @example
 * ```typescript
 * const playlist = ensureScheduleParsed(rawPlaylist);
 * // playlist.schedule is now a typed Schedule object
 * const scheduleType = playlist.schedule?.type ?? ScheduleType.Always;
 * ```
 */
/**
 * Ensure a playlist's schedule is parsed into a typed Schedule object
 * Simple approach: Convert string enums to numbers, merge root properties
 */
export function ensureScheduleParsed(playlist: Playlist): Playlist {
  // No schedule data
  if (!playlist.schedule) {
    return {
      ...playlist,
      schedule: {
        type: ScheduleType.Always,
        scheduleType: "AlwaysSchedule",
        repeat: RepeatType.None,
        priority: PriorityLevel.Normal,
      } as AlwaysSchedule,
    };
  }

  // Schedule already exists - just normalize it
  const schedule = { ...playlist.schedule } as any;

  // ✅ 1. Convert schedule.type string → ScheduleType enum (number)
  if (typeof schedule.type === "string") {
    schedule.type = parseScheduleTypeString(schedule.type);
  }

  // ✅ 2. Add scheduleType discriminator from root (now handles string correctly!)
  if (!schedule.scheduleType) {
    let scheduleTypeString: Schedule["scheduleType"];

    if (typeof playlist.scheduleType === "string") {
      // Backend sent string like "TimeWindow"
      scheduleTypeString = getScheduleTypeNameFromString(playlist.scheduleType);
    } else if (typeof playlist.scheduleType === "number") {
      // Already converted to enum number
      scheduleTypeString = getScheduleTypeName(playlist.scheduleType);
    } else {
      // Fallback to schedule.type
      scheduleTypeString = getScheduleTypeName(
        schedule.type ?? ScheduleType.Always,
      );
    }

    schedule.scheduleType = scheduleTypeString;
  }

  // ✅ 3. Add repeat from root (now handles string correctly!)
  if (schedule.repeat === undefined && playlist.repeat) {
    schedule.repeat =
      typeof playlist.repeat === "string"
        ? parseRepeatType(playlist.repeat)
        : playlist.repeat;
  } else if (schedule.repeat === undefined) {
    schedule.repeat = RepeatType.None;
  }

  // ✅ 4. Add priority from root
  if (schedule.priority === undefined) {
    schedule.priority =
      typeof playlist.priority === "number"
        ? playlist.priority
        : PriorityLevel.Normal;
  }

  return {
    ...playlist,
    schedule,
  };
}

/**
 * Convert schedule type string to enum
 */
export function parseScheduleTypeString(
  typeString: string | number,
): ScheduleType {
  if (typeof typeString === "number") {
    return typeString as ScheduleType;
  }

  const typeMap: Record<string, ScheduleType> = {
    Always: ScheduleType.Always,
    TimeWindow: ScheduleType.TimeWindow,
    PrayerWindow: ScheduleType.PrayerWindow,
    CrossDayTimeWindow: ScheduleType.CrossDayTimeWindow,
    CrossDayPrayerWindow: ScheduleType.CrossDayPrayerWindow,
    HijriDate: ScheduleType.HijriDate,
    GregorianDate: ScheduleType.GregorianDate,
    PrayerBased: ScheduleType.PrayerBased,
    EventBased: ScheduleType.EventBased,
  };

  return typeMap[typeString] ?? ScheduleType.Always;
}

/**
 * Convert repeat string to enum
 */
function parseRepeatType(repeat: string | number): RepeatType {
  if (typeof repeat === "number") {
    return repeat as RepeatType;
  }

  const repeatMap: Record<string, RepeatType> = {
    None: RepeatType.None,
    Daily: RepeatType.Daily,
    Weekly: RepeatType.Weekly,
    Monthly: RepeatType.Monthly,
    Yearly: RepeatType.Yearly,
  };

  return repeatMap[repeat] ?? RepeatType.None;
}

/**
 * Get schedule type discriminator name from ScheduleType enum
 */
function getScheduleTypeName(type: ScheduleType): Schedule["scheduleType"] {
  const typeNames: Record<ScheduleType, Schedule["scheduleType"]> = {
    [ScheduleType.Always]: "AlwaysSchedule",
    [ScheduleType.TimeWindow]: "TimeWindowSchedule",
    [ScheduleType.PrayerWindow]: "PrayerWindowSchedule",
    [ScheduleType.CrossDayTimeWindow]: "CrossDayTimeWindowSchedule",
    [ScheduleType.CrossDayPrayerWindow]: "CrossDayPrayerWindowSchedule",
    [ScheduleType.HijriDate]: "HijriDateSchedule",
    [ScheduleType.GregorianDate]: "GregorianDateSchedule",
    [ScheduleType.PrayerBased]: "PrayerBasedSchedule",
    [ScheduleType.EventBased]: "EventBasedSchedule",
  };

  return typeNames[type] ?? "AlwaysSchedule";
}

/**
 * Map a string to a valid Schedule["scheduleType"] value
 */
function getScheduleTypeNameFromString(type: string): Schedule["scheduleType"] {
  const map: Record<string, Schedule["scheduleType"]> = {
    Always: "AlwaysSchedule",
    TimeWindow: "TimeWindowSchedule",
    PrayerWindow: "PrayerWindowSchedule",
    CrossDayTimeWindow: "CrossDayTimeWindowSchedule",
    CrossDayPrayerWindow: "CrossDayPrayerWindowSchedule",
    HijriDate: "HijriDateSchedule",
    GregorianDate: "GregorianDateSchedule",
    PrayerBased: "PrayerBasedSchedule",
    EventBased: "EventBasedSchedule",
  };
  return map[type] ?? "AlwaysSchedule";
}

/**
 * Parse schedule JSON string to typed Schedule object
 *
 * @param scheduleJson JSON string from backend
 * @returns Typed Schedule object or null if parsing fails
 *
 * @example
 * ```typescript
 * const schedule = parseScheduleJson(playlist.scheduleJson);
 * if (schedule) {
 *   console.log(`Schedule type: ${ScheduleType[schedule.type]}`);
 * }
 * ```
 */
export function parseScheduleJson(
  scheduleJson: string | null | undefined,
): Schedule | null {
  if (!scheduleJson || scheduleJson.trim() === "") {
    return null;
  }

  try {
    const parsed = JSON.parse(scheduleJson);

    // Validate that it has required properties
    if (!isValidSchedule(parsed)) {
      console.error("[parseScheduleJson] Invalid schedule structure:", parsed);
      return null;
    }

    return parsed as Schedule;
  } catch (error) {
    console.error("[parseScheduleJson] JSON parse error:", error);
    return null;
  }
}

/**
 * Validate that a schedule object is well-formed
 *
 * Type guard that checks if an object conforms to Schedule interface requirements.
 *
 * @param schedule Object to validate
 * @returns True if valid Schedule, false otherwise
 *
 * @example
 * ```typescript
 * if (isValidSchedule(data)) {
 *   // TypeScript now knows data is a Schedule
 *   const handler = getHandler(data.type);
 * }
 * ```
 */
export function isValidSchedule(schedule: any): schedule is Schedule {
  if (!schedule || typeof schedule !== "object") {
    return false;
  }

  // Check required base properties
  if (
    typeof schedule.scheduleType !== "string" ||
    typeof schedule.type !== "number" ||
    typeof schedule.repeat !== "number" ||
    typeof schedule.priority !== "number"
  ) {
    return false;
  }

  // Validate schedule type enum value (0-7)
  if (schedule.type < 0 || schedule.type > 7) {
    return false;
  }

  // Validate priority enum value (0-5)
  if (schedule.priority < 0 || schedule.priority > 5) {
    return false;
  }

  return true;
}

// ============================================================================
// SCHEDULE DEFAULTS & FACTORIES
// ============================================================================

/**
 * Create a playlist with a default AlwaysSchedule
 *
 * @param playlist Original playlist
 * @returns Playlist with default Always schedule
 */
function createPlaylistWithDefaultSchedule(playlist: Playlist): Playlist {
  return {
    ...playlist,
    schedule: {
      scheduleType: "AlwaysSchedule",
      type: ScheduleType.Always,
      repeat: 0,
      priority: playlist.priority,
    } as AlwaysSchedule,
  };
}

/**
 * Get default schedule configuration for a given schedule type
 *
 * Useful for:
 * - Creating new playlists in UI
 * - Providing fallback schedules
 * - Testing schedule handlers
 *
 * Azure Best Practice: Factory pattern for object creation
 *
 * @param type Schedule type to create default for
 * @param priority Optional priority level (defaults to Normal)
 * @returns Default schedule configuration
 *
 * @example
 * ```typescript
 * const newPlaylist = {
 *   id: -1,
 *   name: "New Playlist",
 *   schedule: getDefaultSchedule(ScheduleType.TimeWindow, PriorityLevel.High),
 *   // ...other fields
 * };
 * ```
 */
export function getDefaultSchedule(
  type: ScheduleType,
  priority: PriorityLevel = PriorityLevel.Normal,
): Schedule {
  switch (type) {
    case ScheduleType.Always:
      return {
        scheduleType: "AlwaysSchedule",
        type: ScheduleType.Always,
        repeat: 0,
        priority,
      } as AlwaysSchedule;

    case ScheduleType.TimeWindow:
      return {
        scheduleType: "TimeWindowSchedule",
        type: ScheduleType.TimeWindow,
        repeat: 0,
        priority,
        daysOfWeek: [1, 2, 3, 4, 5], // Monday-Friday
        startTime: "09:00:00",
        endTime: "17:00:00",
      } as TimeWindowSchedule;

    case ScheduleType.CrossDayTimeWindow:
      return {
        scheduleType: "CrossDayTimeWindowSchedule",
        type: ScheduleType.CrossDayTimeWindow,
        repeat: 0,
        priority,
        startDay: 5, // Friday
        startTime: "18:00:00",
        endDay: 1, // Monday
        endTime: "06:00:00",
      } as CrossDayTimeWindowSchedule;

    case ScheduleType.PrayerWindow:
      return {
        scheduleType: "PrayerWindowSchedule",
        type: ScheduleType.PrayerWindow,
        repeat: 0,
        priority,
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6], // All days
        startPrayer: "StartFajr",
        endPrayer: "StartDhuhr",
        secondsBeforeStartPrayer: 0,
      } as PrayerWindowSchedule;

    case ScheduleType.CrossDayPrayerWindow:
      return {
        scheduleType: "CrossDayPrayerWindowSchedule",
        type: ScheduleType.CrossDayPrayerWindow,
        repeat: 0,
        priority,
        startDay: 5, // Friday
        startPrayer: "StartEsha",
        endDay: 1, // Monday
        endPrayer: "StartFajr",
        secondsBeforeStartPrayer: 0,
      } as CrossDayPrayerWindowSchedule;

    case ScheduleType.PrayerBased:
      return {
        scheduleType: "PrayerBasedSchedule",
        type: ScheduleType.PrayerBased,
        repeat: 0,
        priority: PriorityLevel.Critical, // Usually critical
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        prayerScheduleType: "before",
        prayers: [
          "StartFajr",
          "StartDhuhr",
          "StartAsr",
          "StartMaghrib",
          "StartEsha",
        ],
        secondsBeforeStartPrayer: 300, // 5 minutes before
        displayDurationSeconds: 60, // 1 minute
        startPrayer: "",
        endPrayer: "",
      } as PrayerBasedSchedule;

    case ScheduleType.HijriDate:
      return {
        scheduleType: "HijriDateSchedule",
        type: ScheduleType.HijriDate,
        repeat: 0,
        priority: PriorityLevel.High,
        months: [9], // Ramadan
        days: "1-30",
        time: undefined,
      } as HijriDateSchedule;

    case ScheduleType.GregorianDate:
      return {
        scheduleType: "GregorianDateSchedule",
        type: ScheduleType.GregorianDate,
        repeat: 0,
        priority: PriorityLevel.High,
        months: [12], // December
        days: "25", // Christmas
        time: undefined,
      } as GregorianDateSchedule;

    case ScheduleType.EventBased:
      return {
        scheduleType: "EventBasedSchedule",
        type: ScheduleType.EventBased,
        repeat: RepeatType.None,
        priority: PriorityLevel.High,
        eventId: undefined,
        eventName: undefined,
        eventStartTime: undefined,
        eventEndTime: undefined,
      } as EventBasedSchedule;

    default:
      console.warn(
        `[getDefaultSchedule] Unknown schedule type: ${type}, returning Always`,
      );
      return getDefaultSchedule(ScheduleType.Always, priority);
  }
}

// ============================================================================
// SCHEDULE MANIPULATION
// ============================================================================

/**
 * Clone a schedule object (deep copy)
 *
 * Useful for editing schedules without mutating the original.
 *
 * @param schedule Schedule to clone
 * @returns Deep copy of schedule
 *
 * @example
 * ```typescript
 * const editedSchedule = cloneSchedule(originalSchedule);
 * editedSchedule.priority = PriorityLevel.High;
 * // originalSchedule is unchanged
 * ```
 */
export function cloneSchedule<T extends Schedule>(schedule: T): T {
  return JSON.parse(JSON.stringify(schedule));
}

/**
 * Compare two schedules for equality
 *
 * Uses JSON serialization for deep comparison.
 * Note: Order of properties matters in this comparison.
 *
 * @param schedule1 First schedule
 * @param schedule2 Second schedule
 * @returns True if schedules are identical
 *
 * @example
 * ```typescript
 * if (!areSchedulesEqual(currentSchedule, savedSchedule)) {
 *   console.log("Schedule has unsaved changes");
 * }
 * ```
 */
export function areSchedulesEqual(
  schedule1: Schedule,
  schedule2: Schedule,
): boolean {
  return JSON.stringify(schedule1) === JSON.stringify(schedule2);
}

/**
 * Batch process multiple playlists to ensure schedules are parsed
 *
 * Useful for processing playlists received from backend API.
 *
 * @param playlists Array of playlists
 * @returns Array with all schedules parsed
 *
 * @example
 * ```typescript
 * const playlists = await fetchPlaylists();
 * const parsedPlaylists = ensureAllSchedulesParsed(playlists);
 * // All playlists now have typed schedule objects
 * ```
 */
export function ensureAllSchedulesParsed(playlists: Playlist[]): Playlist[] {
  return playlists.map(ensureScheduleParsed);
}

/**
 * Stringify a schedule object for backend submission
 *
 * Converts typed Schedule object back to JSON string for API calls.
 *
 * @param schedule Schedule object to stringify
 * @returns JSON string representation
 *
 * @example
 * ```typescript
 * const playlist = {
 *   ...data,
 *   scheduleJson: stringifySchedule(schedule),
 * };
 * await savePlaylist(playlist);
 * ```
 */
export function stringifySchedule(schedule: Schedule): string {
  try {
    return JSON.stringify(schedule);
  } catch (error) {
    console.error("[stringifySchedule] Failed to stringify schedule:", error);
    throw new Error("Failed to serialize schedule for API submission");
  }
}

// ============================================================================
// SCHEDULE DESCRIPTION HELPERS
// ============================================================================

/**
 * Get human-readable description of a schedule
 *
 * Useful for displaying schedule information in UI.
 *
 * @param schedule Schedule to describe
 * @returns Human-readable description string
 *
 * @example
 * ```typescript
 * const description = getScheduleDescription(playlist.schedule);
 * // "Monday-Friday, 9:00 AM - 5:00 PM"
 * ```
 */
export function getScheduleDescription(
  schedule: Schedule | null | undefined,
): string {
  if (!schedule) {
    return "No schedule configured";
  }

  switch (schedule.type) {
    case ScheduleType.Always:
      return "Always active (24/7)";

    case ScheduleType.TimeWindow: {
      const timeWindow = schedule as TimeWindowSchedule;
      const days = timeWindow.daysOfWeek
        .map((d) => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d])
        .join(", ");
      return `${days}, ${formatTime(timeWindow.startTime)} - ${formatTime(
        timeWindow.endTime,
      )}`;
    }

    case ScheduleType.CrossDayTimeWindow: {
      const crossDay = schedule as CrossDayTimeWindowSchedule;
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return `${days[crossDay.startDay]} ${formatTime(crossDay.startTime)} - ${
        days[crossDay.endDay]
      } ${formatTime(crossDay.endTime)}`;
    }

    case ScheduleType.PrayerWindow: {
      const prayer = schedule as PrayerWindowSchedule;
      return `Between ${prayer.startPrayer} and ${prayer.endPrayer}`;
    }

    case ScheduleType.CrossDayPrayerWindow: {
      const crossPrayer = schedule as CrossDayPrayerWindowSchedule;
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return `${days[crossPrayer.startDay]} ${crossPrayer.startPrayer} - ${
        days[crossPrayer.endDay]
      } ${crossPrayer.endPrayer}`;
    }

    case ScheduleType.PrayerBased: {
      const prayerBased = schedule as PrayerBasedSchedule;
      if (prayerBased.prayerScheduleType === "before") {
        return `Before prayers: ${prayerBased.prayers.join(", ")}`;
      } else if (prayerBased.prayerScheduleType === "during-adhan") {
        return `During adhan: ${prayerBased.prayers.join(", ")}`;
      } else if (prayerBased.prayerScheduleType === "after-adhan") {
        return `After adhan: ${prayerBased.prayers.join(", ")}`;
      } else if (
        prayerBased.prayerScheduleType === "after" ||
        prayerBased.prayerScheduleType === "after-end"
      ) {
        return `After prayers end: ${prayerBased.prayers.join(", ")}`;
      } else if (prayerBased.prayerScheduleType === "after-start") {
        const offset = prayerBased.secondsAfterStartPrayer || 0;
        return `After prayers start (+${offset}s): ${prayerBased.prayers.join(
          ", ",
        )}`;
      } else {
        return `Between ${prayerBased.startPrayer} and ${prayerBased.endPrayer}`;
      }
    }

    case ScheduleType.HijriDate: {
      const hijri = schedule as HijriDateSchedule;
      const months = getHijriMonthNames(hijri.months);
      return `Hijri: ${months}, days ${hijri.days}`;
    }

    case ScheduleType.GregorianDate: {
      const gregorian = schedule as GregorianDateSchedule;
      const months = getGregorianMonthNames(gregorian.months);
      return `${months}, days ${gregorian.days}`;
    }

    case ScheduleType.EventBased: {
      const eventBased = schedule as EventBasedSchedule;
      return (
        eventBased.eventName || `Event: ${eventBased.eventId || "Unknown"}`
      );
    }

    default:
      return `Unknown schedule type: ${(schedule as Schedule).type}`;
  }
}

/**
 * Format time string for display (HH:MM:SS -> HH:MM AM/PM)
 */
function formatTime(timeStr: string): string {
  if (!timeStr) return "";

  const [hours, minutes] = timeStr.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;

  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

/**
 * Get Hijri month names for array of month numbers
 */
function getHijriMonthNames(months: number[]): string {
  const names = [
    "Muharram",
    "Safar",
    "Rabi' al-Awwal",
    "Rabi' al-Thani",
    "Jumada al-Awwal",
    "Jumada al-Thani",
    "Rajab",
    "Sha'ban",
    "Ramadan",
    "Shawwal",
    "Dhul-Qa'dah",
    "Dhul-Hijjah",
  ];
  return months.map((m) => names[m - 1]).join(", ");
}

/**
 * Get Gregorian month names for array of month numbers
 */
function getGregorianMonthNames(months: number[]): string {
  const names = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months.map((m) => names[m - 1]).join(", ");
}

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Validate that a playlist has a valid schedule configuration
 *
 * @param playlist Playlist to validate
 * @returns Object with isValid flag and error messages
 *
 * @example
 * ```typescript
 * const validation = validatePlaylistSchedule(playlist);
 * if (!validation.isValid) {
 *   console.error("Invalid schedule:", validation.errors);
 * }
 * ```
 */
export function validatePlaylistSchedule(playlist: Playlist): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!playlist.schedule) {
    errors.push("Schedule configuration is missing");
    return { isValid: false, errors };
  }

  if (!isValidSchedule(playlist.schedule)) {
    errors.push("Schedule structure is invalid");
    return { isValid: false, errors };
  }

  // Type-specific validation can be added here
  // For now, basic validation is done

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Serialize Schedule object to JSON string
 */
export const serializeSchedule = (schedule: Schedule): string => {
  return JSON.stringify(schedule);
};
